import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import React from "react";

import { GenerationFailedTile } from "@/components/Tiles/GenerationFailedTile/GenerationFailedTile";
import { GenerationInProgressTile } from "@/components/Tiles/GenerationInProgressTile/GenerationInProgressTile";
import { GenerationTile } from "@/components/Tiles/GenerationTile/GenerationTile";
import type { GenerationTileAction } from "@/components/Tiles/GenerationTile/types";
import {
  SessionAssetFragment,
  SessionFileFragment,
} from "@/features/forge/forge-session/queries.graphql";
import { ObjectType } from "@/types";
import { getErrorMessage } from "@/utils/inferences";
import { getObjectType } from "@/utils/libraryObjectUtils";

interface ObjectsListTileProps {
  object: SessionFileFragment | SessionAssetFragment;
  onAction?: (
    e: React.MouseEvent,
    action: GenerationTileAction,
    id: string,
  ) => void;
  isSelected?: boolean;
  isSelectable?: boolean;
  isLazyLoaded?: boolean;
  isPlaying?: boolean;
  isControlsSectionHidden?: boolean;
  isButtonsSectionHidden?: boolean;
  isLikeHidden?: boolean;
  isObjectTypeIndicatorHidden?: boolean;
  isLiking?: boolean;
  isFocused?: boolean;
}

export const OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE = "objects-list-tile-image";
export const OBJECTS_LIST_TILE_AUDIO_DRAGGABLE_TYPE = "objects-list-tile-audio";
export const OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE = "objects-list-tile-video";

const objectTypeToDraggableType: Partial<Record<ObjectType, string>> = {
  image: OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE,
  audio: OBJECTS_LIST_TILE_AUDIO_DRAGGABLE_TYPE,
  video: OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE,
};

export const ObjectsListTile = React.memo(function ObjectsListTile(
  props: ObjectsListTileProps,
) {
  const {
    object,
    onAction,
    isSelected = false,
    isSelectable = false,
    isLazyLoaded = false,
    isPlaying = false,
    isControlsSectionHidden = false,
    isButtonsSectionHidden = false,
    isLikeHidden = false,
    isObjectTypeIndicatorHidden = false,
    isLiking = false,
    isFocused = false,
  } = props;

  const typename = object.__typename;
  const inferenceStatus = typename === "File" ? object.inference?.status : null;
  const objectType =
    object.__typename === "File" ? getObjectType(object) : "image";
  const draggableType = objectTypeToDraggableType[objectType];
  const isDraggable =
    draggableType && ["image", "audio", "video"].includes(objectType);

  const {
    listeners,
    setNodeRef: draggableNodeRef,
    isDragging,
  } = useDraggable({
    id: object.id,
    data: {
      type: draggableType,
      item: object,
    },
    disabled: !isDraggable,
  });

  // If the file is a generation, but is not complete, failed, or in progress, don't show the tile
  if (
    typename === "File" &&
    !!inferenceStatus &&
    !["COMPLETE", "FAILED", "IN_PROGRESS"].includes(inferenceStatus)
  ) {
    return null;
  }

  const handleActionForIncompleteFile = (
    e: React.SyntheticEvent | React.MouseEvent,
    action: GenerationTileAction,
  ) => {
    onAction?.(e as React.MouseEvent, action, object.id);
  };

  if (
    typename === "Asset" ||
    !inferenceStatus ||
    inferenceStatus === "COMPLETE"
  ) {
    const Wrapper = isDraggable ? Box : React.Fragment;
    const WrapperProps = isDraggable
      ? {
          ref: draggableNodeRef,
          ...listeners,
          sx: { opacity: isDragging ? 0.5 : 1 },
        }
      : {};

    return (
      <Wrapper {...WrapperProps}>
        <GenerationTile
          object={object}
          isSelected={isSelected}
          isSelectable={isSelectable}
          onAction={onAction}
          isControlsSectionHidden={isControlsSectionHidden}
          isButtonsSectionHidden={isButtonsSectionHidden}
          isLikeHidden={isLikeHidden}
          isObjectTypeIndicatorHidden={isObjectTypeIndicatorHidden}
          isLiking={isLiking}
          isFocused={isFocused}
          videoGenerationTileProps={{
            isPlaying,
            isLazyLoaded,
          }}
          audioGenerationTileProps={{
            isPlaying,
          }}
        />
      </Wrapper>
    );
  } else if (inferenceStatus === "FAILED") {
    const inference = object.inference;

    return (
      <GenerationFailedTile
        objectType={objectType}
        inferenceId={inference?.id ?? ""}
        isSelected={isSelected}
        isSelectable={isSelectable}
        isFocused={isFocused}
        onAction={handleActionForIncompleteFile}
        errorMessage={getErrorMessage(inference)}
      />
    );
  } else if (inferenceStatus === "IN_PROGRESS") {
    const inference = object.inference;
    const inferenceParameters = object.inferenceParameters;

    return (
      <GenerationInProgressTile
        objectType={objectType}
        etaAt={inference?.etaAt ?? new Date().toISOString()}
        inferenceParameters={inferenceParameters ?? { prompt: "" }}
        startedAt={inference?.createdAt ?? new Date().toISOString()}
        url={object.url}
        isSelected={isSelected}
        isSelectable={isSelectable}
        onAction={handleActionForIncompleteFile}
      />
    );
  }
});
